<template>
    <div class="payment-card">
        <div class="payment-card__header">
            <span class="payment-card__icon">💳</span>
            <span class="payment-card__title">Payment Details</span>
        </div>

        <div class="mb-1">
            <label class="payment-label" for="paymentMode">Mode of Payment</label>
            <v-select
                placeholder="Select Cash & Bank Account"
                class="w-100 payment-select"
                v-model="formData.mop_account_head_id"
                name="mop_account_head_id"
                :options="cashAndBank"
                label="name"
                :reduce="name => name.id"
            />
        </div>

        <div class="mb-1">
            <label class="payment-label" for="mopReference">MoP Reference</label>
            <vField
                v-model="formData.receipt_reference"
                name="receipt_reference"
                type="text"
                class="form-control payment-input"
            />
        </div>

        <div class="row mb-1">
            <div class="col-12 col-md-12 mb-1">
                <label class="payment-label" for="receiptAmount">Paid Amount</label>
                <div class="input-highlight input-highlight--green">
                    <vField
                        v-model="formData.payment"
                        name="amount"
                        type="number"
                        class="form-control payment-input text-right"
                        @keyup="validatePaymentAmount"
                    />
                </div>
            </div>
            <div class="col-12 col-md-6">
                <label class="payment-label" for="receiptAmount">Net Payable</label>
                <div class="input-highlight input-highlight--blue">
                    <vField
                        v-model="props.grandTotal"
                        name="total"
                        type="number"
                        class="form-control payment-input text-right"
                        disabled="disabled"
                    />
                </div>
            </div>
            <div class="col-12 col-md-6">
                <label class="payment-label" for="receivedAmount">Total Collection</label>
                <div class="input-highlight input-highlight--purple">
                    <vField
                        v-model="receivedAmount"
                        name="receivedAmount"
                        type="number"
                        class="form-control payment-input text-right"
                    />
                </div>
            </div>
        </div>

        <div class="row mb-1">
            <div class="col-12 col-md-6 d-flex align-items-end">
                <div class="change-amount-badge">
                    <span class="change-amount-badge__label">Change Amount</span>
                    <span class="change-amount-badge__value">{{ changeAmount }} BDT</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import {computed, inject, onMounted, ref, watch} from "vue";
import {useRoute} from "vue-router";
import handleReceipt from "@/services/modules/receipt";


const $router = useRoute();
const {fetchCashAndBank} = handleReceipt();
const formData = inject('formData')
const showError = inject('showError')
const props = defineProps({
    grandTotal: {
        type: Number
    },
    isHospitalInvoice: {
        type: Boolean,
        default: false
    }
})

const companyId = $router.params.companyId;
const cashAndBank = ref([])
const receivedAmount = ref(null)

const setInitialValue = (array, field) => {
    if(!array.length) return;

    if (!formData.value[field]) {
        formData.value[field] = array[0].id;
    }
}

watch(() => props.grandTotal, (newVal) => {
    formData.value.payment = 0;
    if (!props.isHospitalInvoice) return;

    const currentPayment = parseFloat(formData.value.payment || 0);
    const newGrandTotal = parseFloat(newVal || 0);

    if (currentPayment <= 0 || currentPayment === parseFloat(props.grandTotal)) {
        formData.value.payment = newGrandTotal;
    }
});

const totalAmount = computed(() => props.grandTotal)

const validatePaymentAmount = (event) => {
    const amount = event.target.value
    if (amount < 0) {
        showError("Payment Amount cannot be less than 0")
    }

    if (amount > totalAmount.value) {
        formData.value.payment = totalAmount.value
    }
}

const changeAmount = computed(() => {
    if (formData.value.payment > 0 && receivedAmount.value && receivedAmount.value > 0) {
        return receivedAmount.value - formData.value.payment;
    }
    return 0
})

const generateMoneyReceiptNumber = () => {
    setInitialValue(cashAndBank.value, 'mop_account_head_id');
    receivedAmount.value = null;
}

onMounted(() => {
    Promise.all([
        fetchCashAndBank(companyId).then(res => {
            if (res.data) cashAndBank.value = res.data
        }),
        generateMoneyReceiptNumber(),
    ]).then(() => {
        setInitialValue(cashAndBank.value, 'mop_account_head_id');
    })
    .catch(e => {})
})

defineExpose({generateMoneyReceiptNumber})
</script>

<style scoped>
/* ── Card wrapper ── */
.payment-card {
    background: linear-gradient(135deg, #f0f4ff 0%, #fdf0ff 50%, #f0fff8 100%);
    border: 2px solid transparent;
    background-clip: padding-box;
    border-radius: 16px;
    padding: 20px;
    position: relative;
    box-shadow: 0 4px 24px rgba(99, 102, 241, 0.10), 0 1px 4px rgba(0,0,0,0.06);
}

/* Colorful border via pseudo-element */
.payment-card::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 18px;
    background: linear-gradient(135deg, #6366f1, #a855f7, #22d3ee, #34d399);
    z-index: -1;
}

/* ── Header ── */
.payment-card__header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 2px dashed rgba(99, 102, 241, 0.25);
}

.payment-card__icon {
    font-size: 1.4rem;
}

.payment-card__title {
    font-size: 1rem;
    font-weight: 700;
    background: linear-gradient(90deg, #6366f1, #a855f7);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    letter-spacing: 0.3px;
}

/* ── Labels ── */
.payment-label {
    font-size: 0.78rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #6366f1;
    margin-bottom: 4px;
    display: block;
}

/* ── Inputs ── */
.payment-input {
    border-radius: 10px !important;
    border: 1.5px solid #e0e7ff !important;
    background: #ffffff !important;
    transition: border-color 0.2s, box-shadow 0.2s;
    font-weight: 500;
}

.payment-input:focus {
    border-color: #6366f1 !important;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15) !important;
    outline: none !important;
}

.payment-input:disabled {
    background: #f1f5f9 !important;
    color: #475569;
}

/* ── Colored left-border accents for each field group ── */
.input-highlight {
    border-radius: 10px;
    padding: 3px;
}

.input-highlight--blue  { background: linear-gradient(135deg, #bfdbfe, #e0e7ff); }
.input-highlight--green { background: linear-gradient(135deg, #bbf7d0, #d1fae5); }
.input-highlight--purple{ background: linear-gradient(135deg, #e9d5ff, #f3e8ff); }

.input-highlight .form-control {
    margin: 0 !important;
}

/* ── v-select styling ── */
.payment-select :deep(.vs__dropdown-toggle) {
    border-radius: 10px;
    border: 1.5px solid #e0e7ff;
    background: #fff;
    padding: 4px 8px;
}

.payment-select :deep(.vs__dropdown-toggle:focus-within) {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

/* ── Change Amount badge ── */
.change-amount-badge {
    display: flex;
    flex-direction: column;
    /* background: linear-gradient(135deg, #6366f1, #a855f7); */
    border-radius: 12px;
    padding: 10px 18px;
    /* color: #fff; */
    width: 100%;
    box-shadow: 0 4px 14px rgba(99, 102, 241, 0.35);
}

.change-amount-badge__label {
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    opacity: 0.85;
}

.change-amount-badge__value {
    font-size: 1.3rem;
    font-weight: 800;
    letter-spacing: 0.5px;
    margin-top: 2px;
}
</style>
